import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // Import useNavigate instead of useHistory
import Cookies from 'js-cookie'; // Import js-cookie to manage cookies

// Function to manage classNames dynamically
function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Navbar() {
  const [isLoggedIn, setIsLoggedIn] = useState(false); // State to manage login status
  const navigate = useNavigate(); // Use navigate instead of history

  useEffect(() => {
    // Check if token is in cookies
    const token = Cookies.get('token');
    if (token) {
      setIsLoggedIn(true); // If token exists, the user is logged in
    } else {
      setIsLoggedIn(false); // If no token, user is logged out
    }
  }, []);

  // Handle logout functionality
  const handleLogout = () => {
    Cookies.remove('token'); // Remove token from cookies
    Cookies.remove('user'); // Optionally remove user data from cookies
    setIsLoggedIn(false); // Update the login status
    navigate('/login'); // Redirect user to login page using navigate
  };

  // Define navigation items based on login status
  const navigation = [
    { name: 'Home', href: '/', current: false },
    { name: 'About', href: '/about', current: false },
    { name: 'Contact', href: '/contact', current: false },
    { name: isLoggedIn ? 'My Donations' : '', href: isLoggedIn ? '/myDonations' : '', current: false },
    { name: isLoggedIn ? 'My Interests' : '', href: isLoggedIn ? '/myInterestedDonations' : '', current: false },
    { name: isLoggedIn ? 'Sign out' : 'Sign in', href: isLoggedIn ? '#' : '/register', current: false, onClick: isLoggedIn ? handleLogout : undefined },
    { name: isLoggedIn ? '' : 'Log in', href: isLoggedIn ? '' : '/login', current: false },
    { name: 'Donate', href: '/donate', current: true },
    { name: 'Need Food', href: '/foodlist', current: true },
  ];

  return (
    <Disclosure as="nav" className="bg-gray-800">
      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between">
          <div className="absolute inset-y-8 left-0 flex items-center sm:hidden">
            {/* Mobile menu button */}
            <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
              <span className="absolute -inset-0.5" />
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="block h-6 w-6 group-data-[open]:hidden" />
              <XMarkIcon aria-hidden="true" className="hidden h-6 w-6 group-data-[open]:block" />
            </DisclosureButton>
          </div>

          <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
            <div className="flex flex-shrink-0 items-center">
              <Link to={"/"}>
                <h1 className="text-green-400 font-bold text-3xl">
                  <span className="text-gray-200">Last</span>bites
                </h1>
              </Link>
            </div>
          </div>

          <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
            <div className="hidden sm:ml-6 sm:block">
              <div className="flex space-x-4">
                {navigation.map((item) =>
                  item.name && (
                    <Link
                      key={item.name}
                      to={item.href}
                      aria-current={item.current ? 'page' : undefined}
                      className={classNames(
                        item.current ? 'bg-green-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                        'rounded-md px-3 py-2 text-sm font-medium'
                      )}
                      onClick={item.onClick} // Add onClick handler for Sign out
                    >
                      {item.name}
                    </Link>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DisclosurePanel className="sm:hidden">
        <div className="space-y-1 px-2 pb-3 pt-2">
          {navigation.map((item) =>
            item.name && (
              <DisclosureButton
                key={item.name}
                as="a"
                href={item.href}
                aria-current={item.current ? 'page' : undefined}
                className={classNames(
                  item.current ? 'bg-green-600 text-white text-center' : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                  'block rounded-md px-3 py-2 text-base font-medium'
                )}
                onClick={item.onClick} // Add onClick handler for Sign out
              >
                {item.name}
              </DisclosureButton>
            )
          )}
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
}
